import { keepaliveWorkerService } from "@services/keepaliveWorkerService";
import { useAppSelector } from "@store/hooks";
import React from "react";
import {
	cleanupFive9Integration,
	initializeFive9Integration,
} from "../services/five9Integration";

// Extend the Window interface to include triggerFive9Ping
declare global {
	interface Window {
		triggerFive9Ping?: () => boolean;
	}
}

/**
 * Custom hook to manage Five9 WebSocket integration lifecycle
 */
export const useFive9Integration = () => {
	const rightioUserId = useAppSelector(({ state }) => state.UserId);
	const [isInitialized, setIsInitialized] = React.useState<boolean>(false);

	console.log(
		`🔍 [FIVE9_STATE] useFive9Integration - rightioUserId from Redux: ${rightioUserId}`,
	);

	// Initialize Five9 WebSocket integration only after user ID is available
	React.useEffect(() => {
		console.log(
			`🔍 [FIVE9_STATE] useFive9Integration useEffect triggered - rightioUserId: ${rightioUserId}, isInitialized: ${isInitialized}`,
		);

		if (rightioUserId && rightioUserId > 0 && !isInitialized) {
			console.log(
				`🔄 [FIVE9_STATE] User ID available (${rightioUserId}), initializing Five9 integration...`,
			);

			const performInitialization = async () => {
				const success = await initializeFive9Integration(rightioUserId);
				setIsInitialized(success);
				if (success) {
					console.log(
						"🔄 [FIVE9_STATE] ✅ useFive9Integration initialization complete",
					);

					// Start Web Worker keepalive after successful Five9 initialization
					console.log(
						"🤖 [FIVE9_STATE] Starting Web Worker keepalive for tab suspension prevention",
					);

					// Set up callback to trigger Five9 ping when worker sends keepalive tick
					keepaliveWorkerService.setOnKeepaliveTick(() => {
						if (typeof window.triggerFive9Ping === "function") {
							const pingSuccess = window.triggerFive9Ping();
							if (!pingSuccess) {
								console.warn(
									"⚠️ [FIVE9_STATE] Failed to trigger Five9 ping from Web Worker",
								);
							}
						} else {
							console.error(
								"❌ [FIVE9_STATE] triggerFive9Ping function not available",
							);
						}
					});

					// Start keepalive with 30 second interval (Five9 recommends under 60s)
					keepaliveWorkerService.startKeepalive(30000);
				} else {
					console.error(
						"🔄 [FIVE9_STATE] ❌ useFive9Integration initialization failed",
					);
				}
			};

			performInitialization();
		}
	}, [rightioUserId, isInitialized]);

	// Cleanup on unmount
	React.useEffect(() => {
		return () => {
			console.log(
				"🧹 [FIVE9_STATE] Cleaning up Five9 integration and Web Worker",
			);
			cleanupFive9Integration();
			keepaliveWorkerService.terminate();
		};
	}, []);

	return { isInitialized };
};
